import { Link } from 'react-router-dom';
import { focusWithoutScroll } from './focusUtils';

export interface StartOverlayProps {
  title: string;
  description: string;
  /** Optional credit line shown in the overlay (e.g. "Music: NCS Release ..."). */
  attribution?: string;
  /**
   * Mode-specific instruction line. Pulse uses "Press Space or tap to flip
   * on each pad."; Circles uses "Click each circle as it lights up."
   */
  instructionsHint: React.ReactNode;
  isFirstTime: boolean;
  /**
   * First-run helper text shown beneath the Start button (e.g. an
   * explanation of the lead-in or a controls primer). Mode-specific.
   */
  firstTimeHint?: React.ReactNode;
  onStart: () => void;
}

/**
 * Tap-to-start overlay shown before a session begins. Reused across
 * modes; the mode-specific bits (description, instructions hint,
 * first-time copy) are passed in by the parent.
 *
 * Click anywhere on the backdrop (not just the button) starts the run
 * — rhythm players tend to mash space/click before reading.
 */
export function StartOverlay({
  title,
  description,
  attribution,
  instructionsHint,
  isFirstTime,
  firstTimeHint,
  onStart,
}: StartOverlayProps) {
  return (
    <div
      className="play-overlay"
      data-testid="tap-to-start-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onStart();
        }
      }}
    >
      <div className="play-overlay-card">
        <h3>{title}</h3>
        <p className="muted">{description}</p>
        {attribution && (
          <p className="muted faint play-attribution-overlay">{attribution}</p>
        )}
        <p>{instructionsHint}</p>
        <button
          type="button"
          className="btn btn-primary btn-large"
          ref={focusWithoutScroll}
          onClick={onStart}
          data-testid="start-button"
        >
          {isFirstTime ? 'Start (first run!)' : 'Start'}
        </button>
        {isFirstTime && firstTimeHint && (
          <p className="faint" data-testid="first-time-hint">
            {firstTimeHint}
          </p>
        )}
        <p className="faint">
          <Link to="/calibrate">Calibrate audio offset</Link>
        </p>
      </div>
    </div>
  );
}
